import React, { useCallback } from 'react';
import { IconButton, useTheme } from '@mui/material';
import { KeyboardHide } from '@mui/icons-material';
import { Capacitor } from '@capacitor/core';
import { dismissNativeKeyboard, useNativeKeyboard } from '../../contexts/NativeKeyboardContext';
import { hapticLight } from '../../utils/nativeHaptics';

const GAP_ABOVE_KEYBOARD_PX = 16;
const FROM_RIGHT_PX = 16;

/**
 * Capacitor-only: floating glass button 16px above the keyboard to dismiss it.
 * Shown when the native keyboard is visible (keyboard inset from @capacitor/keyboard).
 */
const NativeKeyboardDismissButton: React.FC = () => {
  const theme = useTheme();
  const { isKeyboardVisible, keyboardInsetPx, isDismissButtonSuppressed } = useNativeKeyboard();

  const handlePress = useCallback(() => {
    void hapticLight();
    void dismissNativeKeyboard();
  }, []);

  if (!Capacitor.isNativePlatform() || !isKeyboardVisible || isDismissButtonSuppressed) {
    return null;
  }

  const isDark = theme.palette.mode === 'dark';
  // iOS uses KeyboardResize.None: layout is full-screen, so offset by native keyboard height.
  // Android typically resizes the WebView above the IME; `bottom` is already above the keyboard.
  const bottomPx =
    Capacitor.getPlatform() === 'ios' ? keyboardInsetPx + GAP_ABOVE_KEYBOARD_PX : GAP_ABOVE_KEYBOARD_PX;

  return (
    <IconButton
      aria-label="Close keyboard"
      data-testid="native-keyboard-dismiss"
      onClick={handlePress}
      sx={{
        position: 'fixed',
        right: FROM_RIGHT_PX,
        bottom: bottomPx,
        zIndex: 2000,
        width: 44,
        height: 44,
        p: 0,
        color: isDark ? 'rgba(255, 255, 255, 0.92)' : 'rgba(60, 60, 67, 0.9)',
        bgcolor: isDark ? 'rgba(28, 28, 30, 0.72)' : 'rgba(255, 255, 255, 0.72)',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)'}`,
        boxShadow: isDark
          ? '0 8px 24px rgba(0, 0, 0, 0.45)'
          : '0 8px 24px rgba(0, 0, 0, 0.12)',
        '&:hover': {
          bgcolor: isDark ? 'rgba(44, 44, 46, 0.82)' : 'rgba(255, 255, 255, 0.88)',
        },
      }}
    >
      <KeyboardHide sx={{ fontSize: 22 }} />
    </IconButton>
  );
};

export default NativeKeyboardDismissButton;
